import { SSOLoginResponse, HttpLogin, UsernamePasswordPayload, EmailPasswordPayload } from '@/types/loginResponse'
import { ServiceNames } from '../types/serviceNames'
import { LMS, CONNECTIONS, SECURE_PAYMENTS } from '@/consts/serviceNames'
import { useConfig } from '@/config/useConfig'
import { SsoConfig } from '@/types/interfaces'
import { TokensPayload } from '@/types/tokensPayload'
import { Redirect } from '@/types/redirect'
import { AuthenticationResult } from '@/types/authenticationResult'

type LocationHrefRedirect = (redirectTo: string) => void
type RedirectToRetrieveToken = (authenticationResult: TokensPayload) => Promise<void>
type ChallengeHandler = (username: string, loginResponse: SSOLoginResponse, redirectUrlAfterLogin: string) => void
type UpdatePasswordHandler = (accessToken: string, redirectUrlAfterLogin: string) => void
type UpdatePasswordHttp = (oldPassword: string, accessToken: string, newPassword: string) => Promise<SSOLoginResponse>
type InitRedirectAfterMFA = (username: string, authenticationResult: AuthenticationResult, redirectUrlAfterLogin: string) => void
type MfaHandler = (payload: { username: string, authenticationResult: AuthenticationResult, redirectUrlAfterLogin: string }) => void

const defaultLocationHrefRedirect: LocationHrefRedirect = (redirectTo: string) => { document.location.href = redirectTo }
const state: { accessToken?: string, redirectUrlAfterLogin?: string } = {
  accessToken: undefined,
  redirectUrlAfterLogin: undefined
}

const lmsLogin = async (config: SsoConfig, httpLogin: HttpLogin, payload: UsernamePasswordPayload, { challengeHandler, redirectToRetrieveToken, updatePasswordHandler, mfaHandler }: { challengeHandler: ChallengeHandler, redirectToRetrieveToken: RedirectToRetrieveToken, updatePasswordHandler: UpdatePasswordHandler, mfaHandler: MfaHandler }) => {
  const redirectUrl = config?.api?.lms_redirect_url as string

  const response = await httpLogin.lms(payload)

  if (response.code === 'PasswordExpired' && !response.mfaRequired) {
    updatePasswordHandler(response.accessToken, redirectUrl)
    // We set the email and accessToken in the sessionStorage just to uses it in the changePassword in case the user do a refresh in the page.
    window.sessionStorage.setItem('email', payload.username)
    window.sessionStorage.setItem('accessToken', response.accessToken)
  } else if (response.code === 'EnforceMFA') {
    mfaHandler({ username: payload.username, authenticationResult: response.AuthenticationResult, redirectUrlAfterLogin: redirectUrl })
  } else {
    response.ChallengeName
      ? challengeHandler(payload.username, response, redirectUrl)
      : redirectToRetrieveToken(response.AuthenticationResult)
  }
}

const loginConnection = async (config: SsoConfig, httpLogin: HttpLogin, payload: EmailPasswordPayload, locationHrefRedirect: LocationHrefRedirect) => {
  const loginUrl = config?.api?.connections_login_url as string
  const redirectUrl = config?.api?.connections_redirect_url as string

  const response = await httpLogin.connections(loginUrl, payload)

  const redirectTo = `${redirectUrl}?token=${response.token}${encodeURIComponent('.authorization=')}${encodeURIComponent(response.plainText)}`
  locationHrefRedirect(redirectTo)
}

const loginSecurePayments = async (config: SsoConfig, httpLogin: HttpLogin, payload: UsernamePasswordPayload, locationHrefRedirect: LocationHrefRedirect) => {
  const loginUrl = config?.api?.secure_payments_login_url as string
  const redirectUrl = config?.api?.secure_payments_redirect_url as string
  const signOnUrl = config.api?.secure_payments_sign_on_url as string

  const loginResponse = await httpLogin.securePayments(loginUrl, payload)
  const { token } = await httpLogin.securePaymentsSignOn(signOnUrl, loginResponse.token, loginResponse.secret)

  const redirectTo = `${redirectUrl}/${token}`
  locationHrefRedirect(redirectTo)
}

export const usernamePasswordLogin = (httpLogin: HttpLogin, redirectToRetrieveToken: RedirectToRetrieveToken, challengeHandler: ChallengeHandler, updatePasswordHandler: UpdatePasswordHandler, mfaHandler: MfaHandler, locationHrefRedirect = defaultLocationHrefRedirect) =>
  async (username: string, password: string, selectedService: ServiceNames) => {
    const config = await useConfig()

    if (selectedService === LMS) {
      const payload = { username, password }
      return await lmsLogin(config, httpLogin, payload, { challengeHandler, redirectToRetrieveToken, updatePasswordHandler, mfaHandler })
    }

    if (selectedService === CONNECTIONS) {
      const payload = { email: username, password }
      return await loginConnection(config, httpLogin, payload, locationHrefRedirect)
    }

    if (selectedService === SECURE_PAYMENTS) {
      const payload = { username, password }
      return await loginSecurePayments(config, httpLogin, payload, locationHrefRedirect)
    }

    throw Error(`Service ${selectedService} is not supported`)
  }

type InitUpdateFlow = (accessToken: string, redirectUrlAfterLogin: string) => void

export const updatePasswordHandler = (redirect: Redirect, initUpdatePassword: InitUpdateFlow) =>
  (accessToken: string, redirectUrlAfterLogin: string) => {
    initUpdatePassword(accessToken, redirectUrlAfterLogin)
    redirect('UpdatePasswordView')
  }

export const MfaHandler = (redirect: Redirect, initRedirectAfterMFA: InitRedirectAfterMFA) =>
  async (payload: { username: string, authenticationResult: AuthenticationResult, redirectUrlAfterLogin: string }) => {
    initRedirectAfterMFA(payload.username, payload.authenticationResult, payload.redirectUrlAfterLogin)
    redirect('MFA')
  }

export const initUpdatePassword = (accessToken: string) => {
  state.accessToken = accessToken
}

export const updatePasswordFlow = (updatePasswordHttp: UpdatePasswordHttp) => async (oldPassword: string, newPassword: string) => {
  const accessToken = state.accessToken || window.sessionStorage.getItem('accessToken')
  if (!accessToken) {
    throw Error('Must set access token before changing your password')
  }

  await updatePasswordHttp(oldPassword, accessToken, newPassword)
}
